import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { History, Loader2, Users, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface TeamChangeAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  gameType: 'regular' | 'venue';
}

interface AuditRecord {
  id: string;
  team_side: string;
  old_team_id: number | null;
  old_team_name: string;
  new_team_id: number | null;
  new_team_name: string;
  change_reason: string;
  changed_by: string;
  changed_by_type: string;
  affected_players: any;
  affected_submissions: number;
  created_at: string;
}

export const TeamChangeAuditDialog: React.FC<TeamChangeAuditDialogProps> = ({
  open,
  onOpenChange,
  gameId,
  gameType
}) => {
  const [auditRecords, setAuditRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadAuditRecords();
    }
  }, [open, gameId]);

  const loadAuditRecords = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('team_change_audit')
        .select('*')
        .eq('game_id', gameId)
        .eq('game_type', gameType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAuditRecords(data || []);
    } catch (error) {
      console.error('Error loading audit records:', error);
      toast.error('Failed to load change history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Team Change History
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[500px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : auditRecords.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No team changes recorded</p>
            </div>
          ) : (
            <div className="space-y-4">
              {auditRecords.map((record) => (
                <div
                  key={record.id}
                  className="border rounded-lg p-4 space-y-3 bg-card"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">
                        {record.team_side === 'home' ? 'Home Team' : 'Away Team'}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(record.created_at), 'PPp')}
                      </div>
                    </div>
                  </div>

                  {/* Team Change */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 text-sm">
                      <p className="font-medium">{record.old_team_name}</p>
                      <p className="text-xs text-muted-foreground">Previous</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 text-sm">
                      <p className="font-medium">{record.new_team_name}</p>
                      <p className="text-xs text-muted-foreground">New</p>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Reason:</p>
                    <p className="text-sm">{record.change_reason}</p>
                  </div>

                  {/* Impact */}
                  <div className="flex items-center gap-4 pt-2 border-t text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{record.affected_players?.length || 0} players affected</span>
                    </div>
                    <div>
                      <span>{record.affected_submissions || 0} submissions removed</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
